import React, { MutableRefObject, useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { RadioButton } from 'primereact/radiobutton';
import { Dialog } from 'primereact/dialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tooltip } from 'primereact/tooltip';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import MlxButton from '../../../../components/common/MlxButton/MlxButton';
import MlxSideBar from '../../../../components/common/MlxSideBar/MlxSideBar';
import MlxDropDown from '../../../../components/common/MlxDropDown/MlxDropDown';
import { useFormik } from 'formik';
import {
    _Feature,
    _FeatureForm,
    convertFeatureData,
    createFeature,
    updateFeature,
    FeatureFormSchema,
    getSourceList,
} from '../../../../helpers/subscriptionFeatureHelper';
import { getAllSubscriptionPlans } from '../../../../helpers/subscriptionHelper';
import { SUBSCRIPTION_PLANS_LIST } from '../../../../constants/queryKeys';
import { _ToastMessageProps } from '../../../../constants/staticTypes';
import { AlertVariant } from '../../../../constants/constants';
import HttpServiceHelper from '../../../../helpers/httpServiceHelper';

type Props = {
    showSideBar: boolean;
    setShowSideBar: React.Dispatch<React.SetStateAction<boolean>>;
    feature?: _Feature;
    setEditFeature: React.Dispatch<React.SetStateAction<_Feature | undefined>>;
    featureListQueryRef: MutableRefObject<UseQueryResult<any, unknown>>;
    setToastMessage: React.Dispatch<React.SetStateAction<_ToastMessageProps>>;
};

const AddFeatureSidebar: React.FC<Props> = ({
    showSideBar,
    setShowSideBar,
    feature,
    setEditFeature,
    featureListQueryRef,
    setToastMessage,
}) => {
    const queryClient = useQueryClient();
    const isEditMode = !!feature;

    const featureMutation = useMutation(HttpServiceHelper);

    const [sourceList, setSourceList] = useState<{
        TENANT: Array<{ id: string; name: string; description: string; sourceType: string }>;
        ORGANIZATION: Array<{ id: string; name: string; description: string; sourceType: string }>;
    }>({ TENANT: [], ORGANIZATION: [] });

    const [moduleOptions, setModuleOptions] = useState<Array<{ label: string; value: string }>>([]);
    const [archiveConfirmText, setArchiveConfirmText] = useState('');
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [planOptions, setPlanOptions] = useState<Array<{ label: string; value: string; id: string }>>([]);
    const [selectedPlan, setSelectedPlan] = useState<string>('');
    const [featureOptions, setFeatureOptions] = useState<Array<{ id: string; name: string; description: string; unitList: string[] }>>([]);
    const [associatedPlans, setAssociatedPlans] = useState<
        Array<{
            id: string;
            name: string;
            options: Array<{
                featureOptionId: string;
                featureOptionUnit: string;
                isUnlimited: boolean;
            }>;
        }>
    >([]);

    const handleArchiveToggle = (e: any) => {
        const newValue = e.value;

        // If trying to archive (isActive will become false)
        if (newValue) {
            setShowArchiveDialog(true);
        } else {
            // If trying to unarchive, just toggle directly
            formik.setFieldValue('isActive', !newValue);
        }
    };

    const handleArchiveConfirm = () => {
        if (archiveConfirmText === 'Archive') {
            formik.setFieldValue('isActive', false);
            setArchiveConfirmText('');
            setShowArchiveDialog(false);
        }
    };

    const handleArchiveCancel = () => {
        setArchiveConfirmText('');
        setShowArchiveDialog(false);
    };

    const handleAddPlan = () => {
        if (selectedPlan) {
            const planOption = planOptions.find((p) => p.value === selectedPlan);
            if (planOption && !associatedPlans.some((p) => p.id === selectedPlan)) {
                // Create options for all feature options
                const options = featureOptions.map((featureOption) => {
                    const initialUnit = featureOption.unitList?.[0] || '';
                    const initialIsUnlimited = initialUnit === 'unlimited';

                    return {
                        featureOptionId: featureOption.id,
                        featureOptionUnit: initialUnit,
                        isUnlimited: initialIsUnlimited,
                    };
                });

                setAssociatedPlans([
                    ...associatedPlans,
                    {
                        id: planOption.id,
                        name: planOption.label,
                        options: options,
                    },
                ]);
                setSelectedPlan('');
            }
        }
    };

    const handleRemovePlan = (planId: string) => {
        setAssociatedPlans(associatedPlans.filter((p) => p.id !== planId));
    };

    const handlePlanFieldChange = (planId: string, featureOptionId: string, field: string, value: string | boolean) => {
        setAssociatedPlans((prevPlans) =>
            prevPlans.map((plan) => {
                if (plan.id === planId) {
                    // Check if this feature option already exists in the plan's options
                    const optionIndex = plan.options.findIndex((opt) => opt.featureOptionId === featureOptionId);

                    if (optionIndex !== -1) {
                        // Update existing option
                        const updatedOptions = [...plan.options];
                        updatedOptions[optionIndex] = { ...updatedOptions[optionIndex], [field]: value };
                        return {
                            ...plan,
                            options: updatedOptions,
                        };
                    } else {
                        // Create new option for this feature
                        return {
                            ...plan,
                            options: [
                                ...plan.options,
                                {
                                    featureOptionId: featureOptionId,
                                    featureOptionUnit: field === 'featureOptionUnit' ? (value as string) : '',
                                    isUnlimited: field === 'isUnlimited' ? (value as boolean) : false,
                                },
                            ],
                        };
                    }
                }
                return plan;
            })
        );
    };

    // Fetch source list using useQuery
    useQuery({
        queryKey: ['source-list'],
        queryFn: () => getSourceList(queryClient),
        onSuccess: (response) => {
            if (response?.data?.data) {
                setSourceList({
                    TENANT: response.data.data.TENANT || [],
                    ORGANIZATION: response.data.data.ORGANIZATION || [],
                });
            }
        },
        onError: (error) => console.error('Failed to fetch source list:', error),
        refetchOnWindowFocus: false,
    });

    // Fetch subscription plans list
    useQuery({
        queryKey: [SUBSCRIPTION_PLANS_LIST],
        queryFn: () => getAllSubscriptionPlans(queryClient),
        onSuccess: (response) => {
            if (response?.data?.data) {
                const plans = response.data.data.map((plan: any) => ({
                    label: plan.name || plan.planName,
                    value: plan.id,
                    id: plan.id,
                }));
                setPlanOptions(plans);
            }
        },
        onError: (error) => console.error('Failed to fetch plans list:', error),
        refetchOnWindowFocus: false,
    });

    const formik = useFormik<_FeatureForm>({
        initialValues: convertFeatureData(feature) || {
            name: '',
            lookupKey: '',
            description: '',
            isAddon: false,
            sourceType: 'ORGANIZATION',
            sourceId: '',
            isActive: true,
            metadata: {},
        },
        validationSchema: FeatureFormSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const onSuccess = (data: any) => {
                setToastMessage({
                    show: true,
                    message: data?.message || (isEditMode ? 'Feature updated successfully' : 'Feature created successfully'),
                    variant: AlertVariant.SUCCESS,
                });
                featureListQueryRef.current.refetch();
                onSidebarClose();
            };

            const onError = (message: string, variant: string) => {
                setToastMessage({
                    show: true,
                    message,
                    variant,
                });
            };

            // Prepare form data with associated plans in correct format
            const formData: any = {
                id: values.id,
                name: values.name,
                description: values.description,
                isActive: values.isActive,
                lookupKey: values.lookupKey,
                isAddon: values.isAddon,
                metadata: values.metadata || {},
            };

            // Only include sourceId and sourceType if:
            // 1. Creating a new feature (not edit mode), OR
            // 2. Editing a feature that doesn't already have a sourceId
            if (!isEditMode || !feature?.sourceId) {
                formData.sourceType = values.sourceType;
                formData.sourceId = values.sourceId;
            }

            // Add associatedPlans only if there are any
            if (associatedPlans.length > 0) {
                // Flatten to API format: each entry is one plan-feature combination
                const flattenedPlans: any[] = [];
                associatedPlans.forEach((plan) => {
                    plan.options.forEach((option) => {
                        flattenedPlans.push({
                            planId: plan.id,
                            featureOptionId: option.featureOptionId,
                            featureOptionUnit: option.featureOptionUnit,
                            isUnlimited: option.isUnlimited,
                        });
                    });
                });
                (formData as any).associatedPlans = flattenedPlans;
            }

            if (isEditMode && feature) {
                updateFeature(featureMutation, { ...formData, id: feature.id }, onSuccess, onError);
            } else {
                createFeature(featureMutation, formData, onSuccess, onError);
            }
        },
    });

    // Update module options when sourceType changes
    useEffect(() => {
        const sourceType = formik.values.sourceType as 'ORGANIZATION' | 'TENANT';
        if (sourceType && sourceList[sourceType]) {
            const options = sourceList[sourceType].map((source) => ({
                label: source.name,
                value: source.id,
            }));
            setModuleOptions(options);
            // Clear sourceId when scope changes
            if (formik.values.sourceId) {
                const isValidOption = options.some((opt) => opt.value === formik.values.sourceId);
                if (!isValidOption) {
                    formik.setFieldValue('sourceId', '');
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.sourceType, sourceList]);

    // Load feature options from the current feature being edited
    useEffect(() => {
        if (feature?.featureOptions && Array.isArray(feature.featureOptions)) {
            const options = feature.featureOptions.map((opt: any) => ({
                id: opt.id,
                name: opt.optionName || opt.name,
                description: opt.optionDescription || '',
                unitList: opt.optionUnitList || [],
            }));
            setFeatureOptions(options);
        } else {
            setFeatureOptions([]);
        }
    }, [feature]);

    // Initialize associated plans from feature data when editing
    useEffect(() => {
        if (feature?.associatedPlans && Array.isArray(feature.associatedPlans)) {
            const plans = feature.associatedPlans.map((plan: any) => ({
                id: plan.id,
                name: plan.name,
                options: (plan.planFeatures || []).map((pf: any) => ({
                    featureOptionId: pf.featureOptionId,
                    featureOptionUnit: pf.limitUnit || '',
                    isUnlimited: pf.isUnlimited || false,
                })),
            }));
            setAssociatedPlans(plans);
        } else {
            setAssociatedPlans([]);
        }
    }, [feature]);

    const onSidebarClose = () => {
        setShowSideBar(false);
        setEditFeature(undefined);
        formik.resetForm();
        setAssociatedPlans([]);
        setSelectedPlan('');
    };

    return (
        <MlxSideBar
            visible={showSideBar}
            position="right"
            onHide={() => onSidebarClose()}
            content={({ hide }) => (
                <>
                    <div className="mlx-sidebar-header">
                        <span className="header-title">{isEditMode ? 'Edit Feature' : 'Add New Feature'}</span>
                        <MlxButton rounded text size="large" icon="ri-close-fill" onClick={(event) => hide(event)} className="text-dark" />
                    </div>

                    <form onSubmit={formik.handleSubmit} className="mlx-sidebar-body">
                        <div className="mb-3 d-flex flex-column">
                            <label className="mb-1" htmlFor="name">
                                Feature Name <span className="text-danger">*</span>
                            </label>
                            <InputText
                                id="name"
                                name="name"
                                className={`custom-input-text p-inputtext-sm ${
                                    formik.touched.name && formik.errors.name ? 'border-danger' : ''
                                }`}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                autoComplete="off"
                            />
                            {formik.touched.name && formik.errors.name && <small className="text-danger">{formik.errors.name}</small>}
                        </div>

                        <div className="mb-3 d-flex flex-column">
                            <label className="mb-1" htmlFor="lookupKey">
                                Lookup Key <span className="text-danger">*</span>
                            </label>
                            <InputText
                                id="lookupKey"
                                name="lookupKey"
                                className={`custom-input-text p-inputtext-sm ${
                                    formik.touched.lookupKey && formik.errors.lookupKey ? 'border-danger' : ''
                                }`}
                                value={formik.values.lookupKey}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                autoComplete="off"
                            />
                            {formik.touched.lookupKey && formik.errors.lookupKey && (
                                <small className="text-danger">{formik.errors.lookupKey}</small>
                            )}
                        </div>

                        <div className="mb-3 d-flex flex-column">
                            <label className="mb-2">
                                Scope <span className="text-danger">*</span>
                            </label>
                            <div className="d-flex gap-4">
                                <div className="d-flex align-items-center">
                                    <RadioButton
                                        inputId="scopeOrganization"
                                        name="sourceType"
                                        value="ORGANIZATION"
                                        onChange={formik.handleChange}
                                        checked={formik.values.sourceType === 'ORGANIZATION'}
                                    />
                                    <label htmlFor="scopeOrganization" className="ms-2 mb-0">
                                        Management
                                    </label>
                                </div>
                                <div className="d-flex align-items-center">
                                    <RadioButton
                                        inputId="scopeTenant"
                                        name="sourceType"
                                        value="TENANT"
                                        onChange={formik.handleChange}
                                        checked={formik.values.sourceType === 'TENANT'}
                                    />
                                    <label htmlFor="scopeTenant" className="ms-2 mb-0">
                                        Venue
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3 d-flex flex-column">
                            <label className="mb-1" htmlFor="sourceId">
                                Module <span className="text-danger">*</span>
                            </label>
                            <MlxDropDown
                                id="sourceId"
                                name="sourceId"
                                value={formik.values.sourceId}
                                options={moduleOptions}
                                onChange={(e: any) => formik.setFieldValue('sourceId', e.value)}
                                className={`w-100 ${formik.touched.sourceId && formik.errors.sourceId ? 'p-invalid' : ''}`}
                            />
                            {formik.touched.sourceId && formik.errors.sourceId && (
                                <small className="text-danger">{formik.errors.sourceId}</small>
                            )}
                        </div>

                        {/* <div className="mb-3 d-flex flex-column">
                            <label className="mb-1" htmlFor="associatedPlans">
                                Associated Plans
                            </label>
                            <MultiSelect
                                id="associatedPlans"
                                name="associatedPlans"
                                value={formik.values.metadata?.associatedPlans || []}
                                options={planOptions}
                                onChange={(e: any) => formik.setFieldValue('metadata.associatedPlans', e.value)}
                                className="w-100"
                                display="chip"
                                placeholder="Select plans"
                            />
                        </div> */}

                        {isEditMode && feature?.featureKey && (
                            <div className="mb-3 d-flex flex-column">
                                <label className="mb-1" htmlFor="stripeId">
                                    Stripe ID
                                </label>
                                <InputText
                                    id="stripeId"
                                    name="stripeId"
                                    className="custom-input-text p-inputtext-sm"
                                    value={feature.featureKey}
                                    disabled
                                    autoComplete="off"
                                />
                            </div>
                        )}

                        <div className="mb-3 d-flex flex-column">
                            <label className="mb-1" htmlFor="description">
                                Description <span className="text-danger">*</span>
                            </label>
                            <InputText
                                id="description"
                                name="description"
                                className={`custom-input-text p-inputtext-sm ${
                                    formik.touched.description && formik.errors.description ? 'border-danger' : ''
                                }`}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                autoComplete="off"
                            />
                            {formik.touched.description && formik.errors.description && (
                                <small className="text-danger">{formik.errors.description}</small>
                            )}
                        </div>

                        {/* Associated Plans Section - Only show when editing */}
                        {isEditMode && (
                            <div className="mb-3 associated-plans-section">
                                <label className="mb-2 d-block">Associated Plans</label>
                                <div className="d-flex gap-2 mb-3">
                                    <MlxDropDown
                                        id="planSelect"
                                        value={selectedPlan}
                                        options={planOptions.filter((p) => !associatedPlans.some((ap) => ap.id === p.id))}
                                        onChange={(e: any) => setSelectedPlan(e.value)}
                                        placeholder="Select a plan"
                                        className="flex-grow-1"
                                    />
                                    <MlxButton
                                        icon="ri-check-line"
                                        onClick={handleAddPlan}
                                        disabled={!selectedPlan}
                                        className="btn-success"
                                        type="button"
                                    />
                                </div>

                                {associatedPlans.length > 0 && (
                                    <div className="associated-plans-list">
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="fw-medium">Associated Plans ({associatedPlans.length})</span>
                                            <span className="text-muted small">{feature?.name || ''}</span>
                                        </div>
                                        <Accordion multiple>
                                            {associatedPlans.map((plan) => (
                                                <AccordionTab
                                                    key={plan.id}
                                                    header={
                                                        <div className="d-flex align-items-center justify-content-between w-100 pe-2">
                                                            <span>{plan.name}</span>
                                                            <i
                                                                className="ri-delete-bin-line text-danger"
                                                                style={{ fontSize: '1.1rem', cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleRemovePlan(plan.id);
                                                                }}
                                                            />
                                                        </div>
                                                    }
                                                >
                                                    <div className="plan-config-fields">
                                                        {featureOptions.length > 0 ? (
                                                            featureOptions.map((featureOption) => {
                                                                const optionConfig = plan.options.find(
                                                                    (opt) => opt.featureOptionId === featureOption.id
                                                                );

                                                                return (
                                                                    <div key={featureOption.id} className="mb-3">
                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                            <label className="mb-0">{featureOption.name}</label>
                                                                            {featureOption.unitList && featureOption.unitList.length > 0 ? (
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <MlxDropDown
                                                                                        value={optionConfig?.featureOptionUnit || ''}
                                                                                        options={featureOption.unitList.map((unit) => ({
                                                                                            label: unit,
                                                                                            value: unit,
                                                                                        }))}
                                                                                        onChange={(e: any) => {
                                                                                            const selectedValue = e.value;
                                                                                            // Update both fields at once using functional setState
                                                                                            setAssociatedPlans((prevPlans) =>
                                                                                                prevPlans.map((p) => {
                                                                                                    if (p.id === plan.id) {
                                                                                                        const optionIndex =
                                                                                                            p.options.findIndex(
                                                                                                                (opt) =>
                                                                                                                    opt.featureOptionId ===
                                                                                                                    featureOption.id
                                                                                                            );

                                                                                                        if (optionIndex !== -1) {
                                                                                                            const updatedOptions = [
                                                                                                                ...p.options,
                                                                                                            ];
                                                                                                            updatedOptions[optionIndex] = {
                                                                                                                ...updatedOptions[
                                                                                                                    optionIndex
                                                                                                                ],
                                                                                                                featureOptionUnit:
                                                                                                                    selectedValue,
                                                                                                                isUnlimited:
                                                                                                                    selectedValue ===
                                                                                                                    'unlimited',
                                                                                                            };
                                                                                                            return {
                                                                                                                ...p,
                                                                                                                options: updatedOptions,
                                                                                                            };
                                                                                                        } else {
                                                                                                            return {
                                                                                                                ...p,
                                                                                                                options: [
                                                                                                                    ...p.options,
                                                                                                                    {
                                                                                                                        featureOptionId:
                                                                                                                            featureOption.id,
                                                                                                                        featureOptionUnit:
                                                                                                                            selectedValue,
                                                                                                                        isUnlimited:
                                                                                                                            selectedValue ===
                                                                                                                            'unlimited',
                                                                                                                    },
                                                                                                                ],
                                                                                                            };
                                                                                                        }
                                                                                                    }
                                                                                                    return p;
                                                                                                })
                                                                                            );
                                                                                        }}
                                                                                        style={{ width: '200px' }}
                                                                                        placeholder="Select unit"
                                                                                    />
                                                                                    {featureOption.description && (
                                                                                        <>
                                                                                            <i
                                                                                                className={`ri-information-line feature-option-info-${featureOption.id}`}
                                                                                                style={{
                                                                                                    cursor: 'pointer',
                                                                                                    color: '#6c757d',
                                                                                                    fontSize: '1rem',
                                                                                                }}
                                                                                            />
                                                                                            <Tooltip
                                                                                                target={`.feature-option-info-${featureOption.id}`}
                                                                                                content={featureOption.description}
                                                                                                position="top"
                                                                                            />
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <InputSwitch
                                                                                    checked={optionConfig?.isUnlimited || false}
                                                                                    onChange={(e) => {
                                                                                        handlePlanFieldChange(
                                                                                            plan.id,
                                                                                            featureOption.id,
                                                                                            'isUnlimited',
                                                                                            e.value
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <div className="text-muted">No feature options available</div>
                                                        )}
                                                    </div>
                                                </AccordionTab>
                                            ))}
                                        </Accordion>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Only show archive option if feature is currently active */}
                        {(!isEditMode || (isEditMode && feature?.isActive)) && (
                            <div className="mb-3 d-flex align-items-center justify-content-between">
                                <label className="mb-0">Archive feature</label>
                                <InputSwitch checked={!formik.values.isActive} onChange={handleArchiveToggle} />
                            </div>
                        )}
                    </form>

                    <Dialog
                        visible={showArchiveDialog}
                        onHide={handleArchiveCancel}
                        header="Archive Feature"
                        style={{ width: '450px' }}
                        footer={
                            <div>
                                <MlxButton label="Cancel" onClick={handleArchiveCancel} outlined size="small" />
                                <MlxButton
                                    label="Proceed"
                                    onClick={handleArchiveConfirm}
                                    size="small"
                                    className="ms-2"
                                    disabled={archiveConfirmText !== 'Archive'}
                                />
                            </div>
                        }
                    >
                        <div>
                            <div
                                className="d-flex align-items-start mb-3"
                                style={{ color: '#dc3545', backgroundColor: '#fee', padding: '12px', borderRadius: '4px' }}
                            >
                                <i className="pi pi-info-circle me-2" style={{ fontSize: '1.2rem', marginTop: '2px' }} />
                                <span>Once you archive a feature, you cannot revert it back.</span>
                            </div>
                            <div className="mb-2">Type Archive in the textbox to confirm.</div>
                            <InputText
                                value={archiveConfirmText}
                                onChange={(e) => setArchiveConfirmText(e.target.value)}
                                placeholder='"Archive"'
                                className="w-100"
                                autoFocus
                            />
                        </div>
                    </Dialog>

                    <div className="mlx-sidebar-footer">
                        <MlxButton
                            label="Cancel"
                            onClick={() => onSidebarClose()}
                            icon="ri-close-large-line"
                            iconPos="right"
                            size="small"
                            outlined
                            type="button"
                            disabled={featureMutation.isLoading}
                        />
                        <MlxButton
                            className="ms-2"
                            label="Save"
                            onClick={() => formik.handleSubmit()}
                            icon="ri-check-line"
                            iconPos="right"
                            size="small"
                            type="button"
                            loading={featureMutation.isLoading}
                            disabled={featureMutation.isLoading}
                        />
                    </div>
                </>
            )}
        />
    );
};

export default AddFeatureSidebar;
